import { Storage } from '@capacitor/storage';
import { getDefaultClient, getDefaultEventID, isMultieventEnabled } from '../config/app.config';
import { EVENT_STORAGE_KEYS, USER_STORAGE_KEYS } from '../constants/storage';

export interface TutorialSlideConfig {
  image?: string;
  title: string;
  description?: string;
  buttonLabel?: string;
}

export interface TutorialConfig {
  slides: TutorialSlideConfig[];
}

export interface StartDataPayload {
  version: string;
  tutorial?: TutorialConfig;
  [key: string]: any;
}

export class StartDataService {
  private static instance: StartDataService;
  private static readonly CACHE_KEY = 'startDataCache';
  private static readonly META_KEY = 'startDataMeta';
  private static readonly IMAGE_CACHE_NAME = 'event-start-data-images-v1';
  private static readonly START_DATA_PATH = 'appendpoints/{CODIGO_EVENTO}/start_data.json';
  private currentData: StartDataPayload | null = null;
  private cachedImageUrls = new Map<string, string>();

  private constructor() {}

  static getInstance(): StartDataService {
    if (!StartDataService.instance) {
      StartDataService.instance = new StartDataService();
    }
    return StartDataService.instance;
  }

  async loadLatestStartData(url?: string, eventCode?: string): Promise<StartDataPayload | null> {
    try {
      const startDataUrl = url || this.getDefaultUrl();
      const cached = await this.getCachedData();
      const storedEventCode = (await Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_CODE })).value || '';
      const resolvedEventCode = eventCode || storedEventCode || this.getConfiguredDefaultEventCode() || this.getDevelopmentEventCode();

      // La ruta de start data pertenece a un evento; sin código solo se usa el caché.
      if (startDataUrl.includes('{CODIGO_EVENTO}') && !resolvedEventCode) {
        this.currentData = cached;
        await this.cacheEventImages(cached);
        return cached;
      }

      console.log(`StartDataService: Cargando configuración para el evento ${resolvedEventCode}`);

      // Con una caché válida primero consultamos únicamente la versión. Esto evita
      // descargar el JSON completo cuando la configuración no ha cambiado.
      if (cached) {
        const remoteVersion = await this.fetchRemoteVersion(startDataUrl, resolvedEventCode);
        if (remoteVersion && !this.isVersionNewer(remoteVersion, cached.version)) {
          this.currentData = cached;
          await this.cacheEventImages(cached);
          return cached;
        }
      }

      const remote = await this.fetchRemoteData(startDataUrl, resolvedEventCode);

      if (!remote) {
        this.currentData = cached;
        await this.cacheEventImages(cached);
        return cached;
      }

      const shouldUseRemote = this.shouldUseRemote(remote, cached);
      const selected = shouldUseRemote ? remote : cached;

      if (selected && selected !== cached) {
        await this.saveToCache(selected);
      }

      this.currentData = selected;
      await this.cacheEventImages(selected);
      return selected;
    } catch (error) {
      console.error('StartDataService: Error cargando start data', error);
      const cached = await this.getCachedDataSafely();
      this.currentData = cached;
      await this.cacheEventImages(cached);
      return cached;
    }
  }

  private getDefaultUrl(): string {
    const configuredBaseUrl = process.env.REACT_APP_START_DATA_BASE_URL;
    const baseUrl = configuredBaseUrl || (
      process.env.NODE_ENV === 'development'
        ? 'http://127.0.0.1:8765/miesimposio/'
        : 'https://esimposio.com/miesimposio/'
    );

    return `${baseUrl.replace(/\/$/, '')}/${StartDataService.START_DATA_PATH}`;
  }

  private getDevelopmentEventCode(): string {
    if (process.env.REACT_APP_START_DATA_EVENT_CODE) {
      return process.env.REACT_APP_START_DATA_EVENT_CODE;
    }

    return process.env.NODE_ENV === 'development' ? '3C947BC2' : '';
  }

  private getConfiguredDefaultEventCode(): string {
    return isMultieventEnabled() ? '' : (getDefaultEventID() || '');
  }

  async clearCache(): Promise<void> {
    try {
      await Storage.remove({ key: StartDataService.CACHE_KEY });
      await Storage.remove({ key: StartDataService.META_KEY });
      this.currentData = null;
      this.releaseCachedImageUrls();
    } catch (error) {
      console.error('StartDataService: Error limpiando cache', error);
    }
  }

  /** Configuración del tutorial disponible tras cargar start_data.json. */
  getTutorialConfig(): TutorialConfig | null {
    const tutorial = this.currentData?.tutorial;
    if (!tutorial || !Array.isArray(tutorial.slides) || !tutorial.slides.length) {
      return null;
    }

    const slides = tutorial.slides.filter((slide): slide is TutorialSlideConfig => (
      Boolean(slide) && typeof slide.title === 'string' && slide.title.trim().length > 0
    ));

    return slides.length ? { slides } : null;
  }

  /** Devuelve la copia local de una imagen del evento cuando esté disponible. */
  getCachedImageUrl(imageUrl?: string): string | undefined {
    return imageUrl ? (this.cachedImageUrls.get(imageUrl) || imageUrl) : undefined;
  }

  private async cacheEventImages(payload: StartDataPayload | null): Promise<void> {
    if (!payload || typeof caches === 'undefined') return;

    const imageUrls = this.findImageUrls(payload);
    if (!imageUrls.length) return;

    const imageCache = await caches.open(StartDataService.IMAGE_CACHE_NAME);
    await Promise.all(imageUrls.map(async (imageUrl) => {
      try {
        let response = await imageCache.match(imageUrl);
        if (!response) {
          response = await fetch(imageUrl);
          if (!response.ok) return;
          await imageCache.put(imageUrl, response.clone());
        }

        const imageBlob = await response.blob();
        if (!imageBlob.size) return;

        const previousUrl = this.cachedImageUrls.get(imageUrl);
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        this.cachedImageUrls.set(imageUrl, URL.createObjectURL(imageBlob));
      } catch (error) {
        console.warn(`StartDataService: No se pudo cachear la imagen ${imageUrl}`, error);
      }
    }));
  }

  private findImageUrls(value: unknown, propertyName = ''): string[] {
    if (typeof value === 'string') {
      const isImageProperty = /(?:image|logo|icon|background|banner|cover|photo)$/i.test(propertyName);
      return isImageProperty && /^https?:\/\//i.test(value) ? [value] : [];
    }

    if (!value || typeof value !== 'object') return [];

    if (Array.isArray(value)) {
      return Array.from(new Set(value.flatMap(item => this.findImageUrls(item))));
    }

    return Array.from(new Set(Object.entries(value).flatMap(([key, item]) => this.findImageUrls(item, key))));
  }

  private releaseCachedImageUrls(): void {
    this.cachedImageUrls.forEach(url => URL.revokeObjectURL(url));
    this.cachedImageUrls.clear();
  }

  private async fetchRemoteData(url: string, eventCode: string): Promise<StartDataPayload | null> {
    const requestUrl = await this.buildRequestUrl(url.replace('{CODIGO_EVENTO}', encodeURIComponent(eventCode)));
    const response = await fetch(requestUrl, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`No se pudo cargar start data: ${response.status}`);
    }

    const data = await response.json();
    if (!data || typeof data !== 'object') {
      return null;
    }

    return data as StartDataPayload;
  }

  private async fetchRemoteVersion(url: string, eventCode: string): Promise<string | null> {
    try {
      const requestUrl = await this.buildRequestUrl(url.replace('{CODIGO_EVENTO}', encodeURIComponent(eventCode)));
      const versionUrl = new URL(requestUrl, window.location.origin);
      versionUrl.searchParams.set('version', 'si');

      const response = await fetch(this.isAbsoluteUrl(requestUrl) ? versionUrl.toString() : `${versionUrl.pathname}${versionUrl.search}`, {
        cache: 'no-store'
      });
      if (!response.ok) return null;

      const data = await response.json();
      return data && typeof data.version === 'string' ? data.version : null;
    } catch (error) {
      console.warn('StartDataService: No se pudo consultar la versión remota', error);
      return null;
    }
  }

  private async buildRequestUrl(url: string): Promise<string> {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('client', getDefaultClient());

    const userContext = await this.getLoggedUserContext();
    Object.entries(userContext).forEach(([key, value]) => {
      if (value) {
        requestUrl.searchParams.set(key, value);
      }
    });

    if (this.isAbsoluteUrl(url)) {
      return requestUrl.toString();
    }

    return `${requestUrl.pathname}${requestUrl.search}`;
  }

  private async getLoggedUserContext(): Promise<Record<string, string>> {
    const [
      hasLoggedIn,
      userced,
      userid,
      username,
      eventcode
    ] = await Promise.all([
      Storage.get({ key: USER_STORAGE_KEYS.HAS_LOGGED_IN }),
      Storage.get({ key: USER_STORAGE_KEYS.USER_CED }),
      Storage.get({ key: USER_STORAGE_KEYS.USER_ID }),
      Storage.get({ key: USER_STORAGE_KEYS.USERNAME }),
      Storage.get({ key: EVENT_STORAGE_KEYS.EVENT_CODE })
    ]);

    if (hasLoggedIn.value !== 'true' || !userced.value) {
      return {};
    }

    return {
      ced: userced.value,
      userced: userced.value,
      userid: userid.value || '',
      username: username.value || '',
      eventID: eventcode.value || '',
      eventcode: eventcode.value || ''
    };
  }

  private isAbsoluteUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
  }

  private shouldUseRemote(remote: StartDataPayload | null, cached: StartDataPayload | null): boolean {
    if (!remote) return false;
    if (!cached) return true;

    const remoteVersion = this.parseVersion(remote.version);
    const cachedVersion = this.parseVersion(cached.version);

    if (!remoteVersion || !cachedVersion) {
      return JSON.stringify(remote) !== JSON.stringify(cached);
    }

    return remoteVersion > cachedVersion;
  }

  private parseVersion(version?: string): number | null {
    if (!version) return null;
    const normalized = String(version).replace(/[^0-9.]/g, '');
    const parts = normalized.split('.').filter(Boolean).map(Number);
    if (!parts.length) return null;

    return parts.reduce((acc, value, index) => acc + value / Math.pow(10, index + 1), 0);
  }

  private isVersionNewer(remoteVersion?: string, cachedVersion?: string): boolean {
    const remote = this.parseVersion(remoteVersion);
    const cached = this.parseVersion(cachedVersion);

    // Si el servidor no entrega una versión comparable, se descarga el archivo
    // completo para conservar el comportamiento seguro anterior.
    return remote === null || cached === null || remote > cached;
  }

  private async getCachedData(): Promise<StartDataPayload | null> {
    const cached = await this.getCachedDataSafely();
    return cached;
  }

  private async getCachedDataSafely(): Promise<StartDataPayload | null> {
    try {
      const { value } = await Storage.get({ key: StartDataService.CACHE_KEY });
      if (!value) return null;
      return JSON.parse(value) as StartDataPayload;
    } catch (error) {
      console.warn('StartDataService: No hay caché válido', error);
      return null;
    }
  }

  private async saveToCache(payload: StartDataPayload): Promise<void> {
    try {
      await Storage.set({ key: StartDataService.CACHE_KEY, value: JSON.stringify(payload) });
      await Storage.set({ key: StartDataService.META_KEY, value: JSON.stringify({ updatedAt: new Date().toISOString(), version: payload.version }) });
    } catch (error) {
      console.error('StartDataService: Error guardando caché', error);
    }
  }
}

export const startDataService = StartDataService.getInstance();
